import type { Moment } from 'moment';
import type { ReactNode } from 'react';

export const PERIOD_DATE_TYPES = ['day', 'week', 'month', 'quarter', 'year'] as const;

export type PeriodDateType = (typeof PERIOD_DATE_TYPES)[number];

export const PERIOD_PRESET_IDS = [
  'today',
  'yesterday',
  'currentWeek',
  'previousWeek',
  'last7Days',
  'last30Days',
  'currentMonth',
  'previousMonth',
  'last3Months',
] as const;

export type PeriodPresetId = (typeof PERIOD_PRESET_IDS)[number];

export interface RelativePeriodValue {
  isRelative: true;
  relativeAmount: number;
  dateType: PeriodDateType;
}

export interface AbsolutePeriodValue {
  isRelative: false;
  dateRange: [Moment, Moment] | null;
  /** Kept so Absolute RangePicker can use a matching granularity when desired. */
  dateType?: PeriodDateType;
  /** Identifies which absolute UI mode produced the range. */
  mode?: 'preset' | 'absolute';
  /** Preset selected when `mode` is `preset`. */
  presetId?: PeriodPresetId;
}

export type RelativeAbsolutePeriodValue = RelativePeriodValue | AbsolutePeriodValue;

/** Longest window the period may cover, both ends included, e.g. `{ amount: 3, unit: 'month' }`. */
export interface PeriodMaxSpan {
  amount: number;
  unit: 'day' | 'week' | 'month' | 'year';
}

export interface RelativeAbsolutePeriodFilterProps {
  value: RelativeAbsolutePeriodValue;
  onChange: (next: RelativeAbsolutePeriodValue) => void;
  /** Label above the control. Defaults to i18n `filter.period`. */
  label?: ReactNode;
  dateTypes?: readonly PeriodDateType[];
  minAmount?: number;
  disabledDate?: (date: Moment) => boolean;
  /** Absolute RangePicker: show time selectors. */
  showTime?: boolean;
  /** Absolute RangePicker format. */
  format?: string;
  allowClear?: boolean;
  /** Default absolute range when switching from Relative → Absolute. `null` = no range. */
  defaultAbsoluteRange?: () => [Moment, Moment] | null;
  className?: string;
  disabled?: boolean;
  /**
   * Caps both modes: relative amount per unit and the span the absolute calendar can select.
   * Each mode then shows the exact cap it applies under its controls.
   */
  maxSpan?: PeriodMaxSpan;
  /** Shows presets and a custom relative option inside the Relative tab. */
  showPresets?: boolean;
  /** Restricts historical reports to today, including preset and manual ranges. */
  historicalOnly?: boolean;
  /** Timezone used to calculate presets; defaults to the browser timezone. */
  timeZone?: string;
}
